import { POPUP_PLACEMENTS, popupPlacementSchema, type Popup } from '@avrahami/contracts';
import {
  Cell,
  CheckboxField,
  PageHeader,
  SelectField,
  TextAreaField,
  TextField,
} from '@avrahami/ui';
import { useQuery } from '@tanstack/react-query';
import { useCan } from '../api/session';
import { popups, useStoreTimeZone } from '../api/tenant';
import { CrudSection, submitWith } from '../components/CrudSection';
import { ImageUploadField, WindowFields } from '../components/fields';
import { ActiveBadge } from '../components/status';
import { he } from '../i18n/he';
import { windowText } from '../lib/display';
import { popupCreateRequest, popupDraft, popupUpdateRequest, type PopupDraft } from '../lib/drafts';

export function PopupsPage() {
  const canWrite = useCan('store.promotions.write');
  const list = useQuery(popups.listQuery());
  const timeZone = useStoreTimeZone();
  return (
    <>
      <PageHeader title={he.popups.title} />
      <CrudSection<Popup, PopupDraft>
        title={he.popups.title}
        intro={he.popups.intro}
        entity="popup"
        createLabel={he.popups.create}
        newTitle={he.popups.newTitle}
        editTitle={he.popups.editTitle}
        emptyText={he.popups.empty}
        items={list.data?.items}
        loading={list.isPending}
        loadError={list.error}
        invalidate={[popups.listQuery().queryKey]}
        canWrite={canWrite}
        head={[he.popups.titleField, he.popups.placement, he.popups.window, he.common.status]}
        rowData={(popup) => ({ 'data-title': popup.title })}
        cells={(popup) => (
          <>
            <Cell>{popup.title}</Cell>
            <Cell>{he.placement[popup.placement]}</Cell>
            <Cell className="text-xs">{windowText(popup, timeZone)}</Cell>
            <Cell>
              <ActiveBadge active={popup.isActive} />
            </Cell>
          </>
        )}
        draftOf={(item) => popupDraft(item, timeZone)}
        submit={(draft, popup) =>
          popup === undefined
            ? submitWith(popupCreateRequest(draft, timeZone), popups.create)
            : submitWith(popupUpdateRequest(draft, popup.version, timeZone), (body) =>
                popups.update(popup.id, body),
              )
        }
        remove={(popup) => popups.remove(popup.id)}
        renderForm={({ draft, set, errors }) => (
          <>
            <TextField
              label={he.popups.titleField}
              name="title"
              required
              maxLength={120}
              value={draft.title}
              error={errors['title']}
              onChange={(event) => {
                set({ title: event.target.value });
              }}
            />
            <TextAreaField
              label={he.popups.body}
              name="body"
              maxLength={1000}
              value={draft.body}
              error={errors['body']}
              onChange={(event) => {
                set({ body: event.target.value });
              }}
            />
            <ImageUploadField
              label={he.popups.image}
              value={draft.image}
              error={errors['mediaId']}
              testId="popup-image"
              onChange={(image) => {
                set({ image });
              }}
            />
            <div className="grid gap-4 sm:grid-cols-3">
              <TextField
                label={he.popups.buttonLabel}
                name="buttonLabel"
                maxLength={40}
                value={draft.buttonLabel}
                error={errors['buttonLabel']}
                onChange={(event) => {
                  set({ buttonLabel: event.target.value });
                }}
              />
              <TextField
                label={he.popups.link}
                name="link"
                dir="ltr"
                maxLength={300}
                hint={he.popups.linkHint}
                value={draft.link}
                error={errors['link']}
                onChange={(event) => {
                  set({ link: event.target.value });
                }}
              />
              <SelectField
                label={he.popups.placement}
                name="placement"
                value={draft.placement}
                onChange={(event) => {
                  set({ placement: popupPlacementSchema.parse(event.target.value) });
                }}
              >
                {POPUP_PLACEMENTS.map((placement) => (
                  <option key={placement} value={placement}>
                    {he.placement[placement]}
                  </option>
                ))}
              </SelectField>
            </div>
            <WindowFields
              startsAt={draft.startsAt}
              endsAt={draft.endsAt}
              errors={errors}
              disabled={false}
              onChange={set}
            />
            <CheckboxField
              label={he.popups.isActive}
              checked={draft.isActive}
              onChange={(event) => {
                set({ isActive: event.target.checked });
              }}
            />
          </>
        )}
      />
    </>
  );
}
